// The outline of the block selected by keyboard focus.
//
// A class written on ProseMirror's elements does not last: its DOM observer
// treats outside attribute changes as broken DOM and rebuilds the element,
// without a transaction (so re-adding it on "transaction" does not run), and
// re-adding it from a MutationObserver loops with the rebuild.
//
// So the outline is a decoration. Plugin state holds the block id to outline;
// {@link markBlockFocus} changes it through a transaction with a meta.

import type { Editor } from "@tiptap/core";
import { Extension } from "@tiptap/core";
import { type EditorState, Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { BLOCK_ID_ATTR } from "./block-id";

/** The class on the outlined block (the screen draws the line). */
export const BLOCK_FOCUS_CLASS = "trameria-block-focus";

/** Plugin state: the block id to outline, or null. */
const blockFocusPluginKey = new PluginKey<string | null>("trameriaBlockFocus");

export function createBlockFocusPlugin(): Plugin<string | null> {
  return new Plugin<string | null>({
    key: blockFocusPluginKey,

    state: {
      init: () => null,
      apply(tr, current) {
        const meta = tr.getMeta(blockFocusPluginKey);
        // Kept without a meta (positions are looked up from the doc each time,
        // so a moved block needs no mapping).
        if (meta === undefined) return current;
        return typeof meta === "string" && meta.length > 0 ? meta : null;
      },
    },

    props: {
      decorations(state) {
        const blockId = blockFocusPluginKey.getState(state) ?? null;
        if (blockId === null) return null;
        let found: Decoration | null = null;
        // Ids exist only at depth 0.
        state.doc.forEach((node, offset) => {
          if (found === null && node.attrs[BLOCK_ID_ATTR] === blockId)
            found = Decoration.node(offset, offset + node.nodeSize, {
              class: BLOCK_FOCUS_CLASS,
            });
        });
        return found === null ? null : DecorationSet.create(state.doc, [found]);
      },
    },
  });
}

/** The block id outlined now, or null. */
export function blockFocusOf(state: EditorState): string | null {
  return blockFocusPluginKey.getState(state) ?? null;
}

/**
 * Sets the outlined block (null clears it). Does nothing when unchanged: the
 * caller also re-applies it on every transaction, and dispatching anyway would
 * loop.
 */
export function markBlockFocus(editor: Editor, blockId: string | null): void {
  if (editor.isDestroyed) return;
  const { view } = editor;
  const next = blockId !== null && blockId.length > 0 ? blockId : null;
  if (blockFocusOf(view.state) === next) return;
  view.dispatch(view.state.tr.setMeta(blockFocusPluginKey, next));
}

/** {@link createBlockFocusPlugin} as an extension. */
export const BlockFocusPlugin = Extension.create({
  name: "blockFocusPlugin",

  addProseMirrorPlugins() {
    return [createBlockFocusPlugin()];
  },
});
